import {
    APP_EMITTER_EVENTS,
    AppEmitter,
    CircularProgress,
    LinearProgress,
    ModalContent,
    modalContext,
    platformContext
} from "shared";
import {
    ComponentTypes,
    externalFormProtectTypes,
    ITemplate,
    ITemplateComponent,
    isTemplateFieldVisible,
    templatesService
} from "entities/template";
import { authContext } from "entities/user";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import cn from "classnames";
import { Platform } from "@yandex-int/hr-components/lib";
import { IValidationFunc } from "../../lib/types/validation";
import { useComponentsTranslations } from "../../lib/i18n";
import { setLocalStorageFormRecord } from "../../lib/utils/record_localstorage";
import { ExternalUpdateMode } from "../../lib/types/share-state";
import { useDocumentFormData } from "../../lib/hooks/useDocumentFormData";
import { isDocumentFormComponent } from "../../lib/utils/service-component-rules";
import { DocumentFormComponent } from "./components/document_form_component";
import styles from "./document_form.module.scss";

interface IDocumentFormProps {
    components: ITemplateComponent[];
    enLanguageEnabled: boolean;
    onExternalUpdateRecordChange: (record: Record<string, any>) => void;
    template: ITemplate;
    formRecord: Record<string, any>;
    externalUpdateRecord: Record<string, any>;
    onFormRecordChange: (recordTransaction: Record<string, any>, clearForm?: boolean) => void;
    triggerDocumentValidate: number;
    externalUpdateMode: ExternalUpdateMode;
    setTriggerDocumentValidate: React.Dispatch<React.SetStateAction<number>>,
    onShowPreviewChange: (show: boolean) => void;
}

export const DocumentForm: React.FC<IDocumentFormProps> = React.memo(({
                                                                          components,
                                                                          enLanguageEnabled,
                                                                          template,
                                                                          formRecord,
                                                                          triggerDocumentValidate,
                                                                          setTriggerDocumentValidate,
                                                                          externalUpdateRecord,
                                                                          externalUpdateMode,
                                                                          onExternalUpdateRecordChange,
                                                                          onFormRecordChange,
                                                                          onShowPreviewChange
                                                                      }) => {
    const [validationFunctions, setValidationFunctions] = useState<Record<string, IValidationFunc>>({});
    const [savingExternal, setSavingExternal] = useState<boolean>(false);
    const { i18n } = useTranslation();
    const t = useComponentsTranslations();
    const userInfo = useContext(authContext);
    const platform = useContext(platformContext);
    const { close, openModal } = useContext(modalContext);
    const { organizations, businessGroups } = useDocumentFormData();
    const { extUserId } = useParams();
    const [externalLoading, setExternalLoading] = useState<boolean>(false);
    const isDesktop = useMemo(() => {
        return platform === Platform.DESKTOP;
    }, [platform]);

    useEffect(() => {
        setTriggerDocumentValidate(0);
        if (userInfo?.isExternal) {
            setExternalLoading(true);
            templatesService.getPredefinedExternalData(template.id, extUserId).then((res) => {
                setTimeout(() => {
                    setExternalLoading(false);
                    onExternalUpdateRecordChange(res);
                    if (components.some((c) => c.type === ComponentTypes.preview)) {
                        onShowPreviewChange(true);
                    }
                }, 500);
            });
        } else {
            if (extUserId) {
                setExternalLoading(true);
                const promises = [templatesService.getExtDefinedData(template.id, extUserId), templatesService.getExtProtectedData(template.id, extUserId)];
                Promise.all(promises).then(([data, protectedData]) => {
                    setTimeout(() => {
                        setExternalLoading(false);
                        onExternalUpdateRecordChange({ ...data, ...protectedData });
                    }, 500);
                });
            }
        }
    }, []);

    const fieldVisibilityContext = useMemo(() => ({
        formRecord,
        components,
        template,
        enLanguageEnabled,
        language: i18n.language,
        isExternalUser: !!userInfo?.isExternal,
    }), [formRecord, components, template, enLanguageEnabled, i18n.language, userInfo?.isExternal]);

    const isFieldVisible = (component: ITemplateComponent): boolean => {
        return isTemplateFieldVisible(component, fieldVisibilityContext);
    };

    const componentGroups: ITemplateComponent[][] = useMemo(() => {
        const groups: ITemplateComponent[][] = [];
        let currentGroup: ITemplateComponent[] = [];
        components
            .filter((c) => isDocumentFormComponent(c.type))
            .forEach((component) => {
                currentGroup.push(component);
                // @ts-ignore
                if (!(component.hasOwnProperty("isRowGroup") && component.isRowGroup) && currentGroup.length > 0) {
                    groups.push([...currentGroup]);
                    currentGroup = [];
                }
            });
        if (currentGroup.length > 0) {
            groups.push([...currentGroup]);
        }

        return groups;
    }, [components]);

    const onValidationRegister = useCallback((validationFunc: IValidationFunc, tag: string) => {
        setValidationFunctions((prevProps) => {
            const updatedProps = { ...prevProps };
            updatedProps[tag] = validationFunc;

            return updatedProps;
        });
    }, []);

    const onSaveExternalForm = () => {
        onValidationCheck().then((isValid) => {
            setLocalStorageFormRecord(template.id, formRecord);
            if (!isValid) {
                openErrorInFieldsModal();
            } else {
                saveExternalForm();
            }
        });
    };

    const openErrorInFieldsModal = () => {
        openModal(<ModalContent close={close}
                                title={t("errorInFieldModalTitle")}
                                confirmText={t("yesContinue")}
                                cancelText={t("noFill")}
                                onCancel={close}
                                onConfirm={saveExternalForm}
                                showButtonGroup={true}>
        </ModalContent>);
    };

    const saveExternalForm = () => {
        setSavingExternal(true);
        templatesService.saveExternalForm(template.id, extUserId, formRecord).then((res) => {
            AppEmitter.emit(APP_EMITTER_EVENTS.replaceUuids);
            if (res) {
                (async () => {
                    await navigator.clipboard.writeText(res);
                })();
                toast.success(
                    <a target="_blank" rel="noopener noreferrer" href={res} className={styles.link}>
                        {t("ext.link")}
                    </a>);
            } else {
                toast.error(t("ext.linkError"));
            }
        }).catch(e => {
            toast.error(t("ext.linkError"));
        }).finally(() => {
            setSavingExternal(false);
        });
    };

    const isReadonly = (component: ITemplateComponent): boolean => {
        return isExternalProtected(component) || !!component?.readonly;
    };

    const isExternalProtected = (component: ITemplateComponent): boolean => {
        return !!userInfo?.isExternal && component.protect === externalFormProtectTypes.gray;
    };

    const onValidationCheck = useCallback(() => {
        setTriggerDocumentValidate((state) => state + 1);
        return Promise.all(Object.entries(validationFunctions).map(([key, f]) => {
            return f().then((v) => {
                if (!v) {
                    console.error(`Validation error in component ${key}`);
                }
                return v;
            });
        })).then(res => {
            return res.every(v => v);
        });
    }, [validationFunctions]);

    return <div className={isDesktop ? styles.container : styles.containerMobile}>
        {externalLoading && isDesktop && <div className={styles.loader}>
            <CircularProgress size={45}/>
        </div>}
        {externalLoading && !isDesktop && <LinearProgress/>}
        {componentGroups.map((componentGroup, index) =>
            <div className={cn(styles.componentRow, !isDesktop ? styles.componentRowMobile : "")}
                 key={index}>
                {componentGroup.map((component, key) => {
                    return isFieldVisible(component) ? <div
                        className={cn(styles.componentItem, componentGroup.length > 1 && styles.componentItemGroup)}
                        key={key}>
                        <DocumentFormComponent
                            triggerDocumentValidate={triggerDocumentValidate}
                            onValidationCheck={onValidationCheck}
                            template={template}
                            components={components}
                            labelShort={componentGroup.length > 1}
                            externalUpdateMode={externalUpdateMode}
                            isExternalUser={!!userInfo?.isExternal}
                            readonly={isReadonly(component)}
                            isExternalProtected={isExternalProtected(component)}
                            onValidationRegister={onValidationRegister}
                            externalUpdateRecord={externalUpdateRecord}
                            organizations={organizations}
                            businessGroups={businessGroups}
                            formRecord={formRecord}
                            component={component}
                            key={component.id}
                            onSaveExternalForm={onSaveExternalForm}
                            savingExternal={savingExternal}
                            enLanguageEnabled={enLanguageEnabled}
                            onRecordChange={onFormRecordChange}/>
                    </div> : null;
                })}
            </div>
        )}
    </div>;
});
